import { useState, useEffect, useMemo } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  TextInput,
  Checkbox,
  Paper,
  SimpleGrid,
  Button,
  Group,
  Stack,
  Text,
  Radio,
  Switch,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useCanOrder } from './shared/useCanOrder'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { validateDate } from './shared/formValidators'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapCologuardToPdf } from './pdf/mapCologuardToPdf'
import { COLOGUARD_QUESTIONNAIRE_ID, createInitialCologuardFormState } from './cologuardFormData'
import type { CologuardFormState } from './cologuardFormData'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import { OrderReviewSummary } from './shared/OrderReviewSummary'
import type { ReviewSection } from './shared/OrderReviewSummary'
import { SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'
import type { CartItem } from './shared/OrderCart'

const COLOGUARD_CART_ITEM: CartItem = {
  id: 'cologuard',
  code: 'cologuard',
  name: 'Cologuard (Stool DNA + FIT)',
  category: 'Screening',
}

type Props = {
  patient: Patient
  onBack: () => void
}

export const CologuardRequisitionForm = ({ patient, onBack }: Props) => {
  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<CologuardFormState>({
    initialValues: {
      ...createInitialCologuardFormState(),
      // Keep shipping address editable (auto-populated from patient address)
      shippingAddress: patientDefaults.address,
      shippingCityStateZip: patientDefaults.cityStateZip,
    },
    validateInputOnBlur: true,
    validate: {
      providerSignatureDate: (v: string) => validateDate(v),
      shippingAddress: (v: string) => (!v?.trim() ? 'Shipping address is required' : null),
      shippingCityStateZip: (v: string) => (!v?.trim() ? 'City, State, ZIP is required' : null),
      diagnosisText: (v: string) => (!v?.trim() ? 'At least one diagnosis code is required' : null),
      billingAddress: (v: string, values: CologuardFormState) =>
        !values.sameAsShipping && !v?.trim() ? 'Billing address is required' : null,
      billingCityStateZip: (v: string, values: CologuardFormState) =>
        !values.sameAsShipping && !v?.trim() ? 'Billing City, State, ZIP is required' : null,
      insuranceType: (v: string, values: CologuardFormState) =>
        values.billInsurance === 'yes' && !v ? 'Insurance type is required' : null,
    },
  })

  useEffect(() => {
    if (providerDefaults.organizationName) {
      form.setValues({
        healthcareOrgName: providerDefaults.organizationName,
        locationAddress: providerDefaults.orgAddress,
        locationCityStateZip: [
          providerDefaults.orgCity,
          providerDefaults.orgState,
          providerDefaults.orgZip,
        ]
          .filter(Boolean)
          .join(', '),
        phoneNumber: providerDefaults.orgPhone,
        secureFaxNumber: providerDefaults.orgFax,
      })
    }
  }, [providerDefaults.organizationName]) // eslint-disable-line react-hooks/exhaustive-deps

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: COLOGUARD_QUESTIONNAIRE_ID,
    generatePdfBytes: () =>
      generateBeHumanOrderPdf(mapCologuardToPdf(form.values, autoPopulateData)),
    buildFileName: () =>
      `Cologuard_Order_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () => form.values as unknown as Record<string, unknown>,
    vendorName: 'Cologuard',
    laboratoryOrganizationName: 'Exact Sciences (Cologuard)',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
  })

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.providerSignatureDataUrl) {
      form.setFieldValue('providerSignatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'providerSignatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitOrder = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.providerSignatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('providerSignatureDataUrl', dataUrl)
    form.setFieldValue('providerSignatureDate', date)
    signature.closeSignatureModal()
  }

  const [showReview, setShowReview] = useState(false)

  const reviewSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = []
    const shipping = [
      { label: 'Address', value: form.values.shippingAddress },
      { label: 'PO Box / Apt', value: form.values.poBoxApt },
      { label: 'City/State/ZIP', value: form.values.shippingCityStateZip },
    ]
    if (shipping.some((i) => i.value)) {
      sections.push({ title: 'Shipping Address', items: shipping })
    }
    if (!form.values.sameAsShipping) {
      const billing = [
        { label: 'Address', value: form.values.billingAddress },
        { label: 'City/State/ZIP', value: form.values.billingCityStateZip },
      ]
      if (billing.some((i) => i.value)) {
        sections.push({ title: 'Billing Address', items: billing })
      }
    }
    if (form.values.languagePreference) {
      sections.push({
        title: 'Language',
        items: [{ label: 'Preference', value: form.values.languagePreference }],
      })
    }
    const races = [
      form.values.raceWhite && 'White',
      form.values.raceBlack && 'Black or African-American',
      form.values.raceAsian && 'Asian',
      form.values.raceNativeHawaiian && 'Native Hawaiian or other Pacific Islander',
      form.values.raceAmericanIndian && 'American Indian or Alaska Native',
      form.values.raceOther &&
        (form.values.raceOtherText ? `Other: ${form.values.raceOtherText}` : 'Other'),
    ].filter(Boolean) as string[]
    const hispanicLatino =
      form.values.hispanicLatino === 'yes' ? 'Yes' : form.values.hispanicLatino === 'no' ? 'No' : ''
    const ethnicityRace = [
      { label: 'Hispanic or Latino', value: hispanicLatino },
      { label: 'Race', value: races.join(', ') },
    ]
    if (ethnicityRace.some((i) => i.value)) {
      sections.push({ title: 'Patient Ethnicity and Race', items: ethnicityRace })
    }
    if (form.values.billInsurance === 'yes') {
      const insurance = [
        { label: 'Insurance Type', value: form.values.insuranceType },
        { label: 'Claims Address', value: form.values.claimsSubmissionAddress },
        { label: 'Prior Auth Code', value: form.values.priorAuthCode },
      ]
      sections.push({ title: 'Insurance / Billing', items: insurance })
    }
    return sections
  }, [form.values])

  const handleReviewOrder = () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before reviewing.',
        color: 'red',
      })
      return
    }
    if (!form.values.providerSignatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before reviewing.',
        color: 'red',
      })
      return
    }
    setShowReview(true)
  }

  return (
    <OrderFormLayout
      cart={<OrderCart items={[COLOGUARD_CART_ITEM]} onRemove={() => {}} vendorLabel="Cologuard" />}
      header={
        <FormHeader
          title="Cologuard Order Requisition"
          subtitle="Exact Sciences Laboratories — Stool-based DNA test with hemoglobin immunoassay"
          onBack={showReview ? () => setShowReview(false) : onBack}
          onPreview={submission.handlePreviewPdf}
          onSubmit={handleSubmitOrder}
          exporting={submission.exporting}
          submitting={submission.submitting}
          orderSubmitted={submission.orderSubmitted}
          canSubmit={isPractitioner}
          canOrderLoading={canOrderLoading}
          hideActions
          submitTooltip="Practitioner permissions required to submit orders"
          labError={submission.labError}
          orgError={orgError}
          warnings={insuranceWarning ? [insuranceWarning] : []}
        />
      }
    >
      {showReview ? (
        <OrderReviewSummary
          vendorName="Cologuard"
          tests={[COLOGUARD_CART_ITEM]}
          diagnosisText={form.values.diagnosisText}
          sections={reviewSections}
          signatureDataUrl={form.values.providerSignatureDataUrl}
          signatureDate={form.values.providerSignatureDate}
          onEdit={() => setShowReview(false)}
          onSubmit={handleSubmitOrder}
          onPreview={submission.handlePreviewPdf}
          submitting={submission.submitting}
          exporting={submission.exporting}
          orderSubmitted={submission.orderSubmitted}
          isPractitioner={isPractitioner}
          canOrderLoading={canOrderLoading}
          labError={submission.labError}
          orgError={orgError}
        />
      ) : (
        <Stack gap="lg">
          {/* ===== ICD-10 CODE ===== */}
          <DiagnosisCodesSection
            value={form.values.diagnosisText}
            defaultCodes={diagnosisDefaults}
            onChange={(v) => {
              form.setFieldValue('diagnosisText', v)
              form.clearFieldError('diagnosisText')
            }}
            error={form.errors.diagnosisText}
          />

          {/* ===== SHIPPING ADDRESS ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              SHIPPING ADDRESS
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              Kit shipped to patient. Verify and update if needed.
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Address" {...form.getInputProps('shippingAddress')} />
              <TextInput label="PO Box / Apt #" {...form.getInputProps('poBoxApt')} />
              <TextInput label="City, State, Zip" {...form.getInputProps('shippingCityStateZip')} />
            </SimpleGrid>

            <Group mt="md" mb="xs">
              <Text fw={500} size="sm">
                Billing Address
              </Text>
              <Switch
                label="Same as Shipping"
                checked={form.values.sameAsShipping}
                onChange={(e) => {
                  const checked = e.target.checked
                  const v = form.values
                  form.setValues({
                    sameAsShipping: checked,
                    billingAddress: checked ? v.shippingAddress : v.billingAddress,
                    billingCityStateZip: checked ? v.shippingCityStateZip : v.billingCityStateZip,
                  })
                }}
              />
            </Group>
            {!form.values.sameAsShipping && (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Address" {...form.getInputProps('billingAddress')} />
                <TextInput
                  label="City, State, Zip"
                  {...form.getInputProps('billingCityStateZip')}
                />
              </SimpleGrid>
            )}

            <Radio.Group
              label="Language Preference"
              {...form.getInputProps('languagePreference')}
              mt="md"
            >
              <Group mt={4}>
                <Radio value="english" label="English" />
                <Radio value="spanish" label="Spanish" />
                <Radio value="other" label="Other" />
              </Group>
            </Radio.Group>
          </Paper>

          {/* ===== ETHNICITY & RACE ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="xs" className="text-[#6B21A8]">
              PATIENT ETHNICITY AND RACE
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              The completion of this section is optional.
            </Text>
            <Radio.Group
              label="Is your patient of Hispanic or Latino origin or descent?"
              {...form.getInputProps('hispanicLatino')}
            >
              <Group mt={4}>
                <Radio value="yes" label="Yes" />
                <Radio value="no" label="No" />
              </Group>
            </Radio.Group>
            <Text size="sm" fw={500} mt="sm" mb="xs">
              Please mark one or more to indicate your patient&apos;s race:
            </Text>
            <Group gap="md">
              <Checkbox label="White" {...form.getInputProps('raceWhite', { type: 'checkbox' })} />
              <Checkbox
                label="Black or African-American"
                {...form.getInputProps('raceBlack', { type: 'checkbox' })}
              />
              <Checkbox label="Asian" {...form.getInputProps('raceAsian', { type: 'checkbox' })} />
              <Checkbox
                label="Native Hawaiian or other Pacific Islander"
                {...form.getInputProps('raceNativeHawaiian', { type: 'checkbox' })}
              />
              <Checkbox
                label="American Indian or Alaska Native"
                {...form.getInputProps('raceAmericanIndian', { type: 'checkbox' })}
              />
              <Checkbox label="Other" {...form.getInputProps('raceOther', { type: 'checkbox' })} />
            </Group>
            {form.values.raceOther && (
              <TextInput
                label="Please specify"
                placeholder="Enter race/ethnicity..."
                mt="xs"
                {...form.getInputProps('raceOtherText')}
              />
            )}
          </Paper>

          {/* ===== BILLING OPTIONS ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              BILLING OPTIONS
            </Text>
            <Radio.Group
              label="Does patient wish Exact Sciences to bill their insurance?"
              {...form.getInputProps('billInsurance')}
            >
              <Group mt={4}>
                <Radio value="yes" label="Yes" />
                <Radio value="no" label="No (patient will self-pay)" />
              </Group>
            </Radio.Group>

            {form.values.billInsurance === 'yes' && (
              <Stack gap="sm" mt="md">
                <Radio.Group label="Insurance Type" {...form.getInputProps('insuranceType')}>
                  <Group mt={4} gap="sm">
                    <Radio value="private" label="Private" />
                    <Radio value="medicare" label="Medicare" />
                    <Radio value="medicareAdvantage" label="Medicare Advantage" />
                    <Radio value="medicaid" label="Medicaid" />
                    <Radio value="tricare" label="Tricare" />
                  </Group>
                </Radio.Group>
                <TextInput
                  label="Claims Submission Address"
                  {...form.getInputProps('claimsSubmissionAddress')}
                />
                <TextInput
                  label="Prior-Authorization Code (if available)"
                  {...form.getInputProps('priorAuthCode')}
                />
              </Stack>
            )}
          </Paper>

          {/* ===== PROVIDER SIGNATURE ===== */}
          <SignatureSection
            signatureDataUrl={form.values.providerSignatureDataUrl}
            signatureDate={form.values.providerSignatureDate}
            savedSignature={signature.savedSignature}
            signatureModalOpen={signature.signatureModalOpen}
            onOpenModal={signature.openSignatureModal}
            onCloseModal={signature.closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            onSaveSignature={signature.handleSaveSignature}
            certificationText="I am a licensed healthcare provider authorized to order Cologuard. This test is medically necessary and the patient is eligible to use Cologuard."
          />

          {/* ===== ACTIONS ===== */}
          <Group justify="flex-end" pb="xl">
            <Button variant="default" onClick={onBack}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={submission.handlePreviewPdf}
              loading={submission.exporting}
            >
              Download PDF
            </Button>
            <TooltipButton
              color="teal"
              disabled={submission.orderSubmitted || !form.values.providerSignatureDataUrl}
              disabledReason={
                !form.values.providerSignatureDataUrl ? SIGNATURE_REQUIRED_TOOLTIP : null
              }
              onClick={handleReviewOrder}
            >
              {submission.orderSubmitted ? 'Order Submitted' : 'Review Order'}
            </TooltipButton>
          </Group>
        </Stack>
      )}
    </OrderFormLayout>
  )
}
